/**
 * Streaming health watchdog contract. The monitor samples every stage of the
 * live pipeline about once per second, on a clock that keeps running when the
 * tab is hidden, turns the samples into issues ("the stream is frozen and
 * nobody was told" must be impossible), and attempts recovery where it can.
 *
 * It measures; it never renders or mixes. All inputs arrive through HealthDeps
 * so the monitor is testable without a browser.
 */
import type { Logger, Unsubscribe } from '../core/services';
import type { PeerStats } from '../net/api';
import type { RenderStats } from '../render/api';

export interface VideoHealth {
  targetFps: number;
  /** Frames the compositor composed per second over the last interval. */
  producedFps: number;
  /** Frames accepted by the program video track per second (produced minus dropped at the output). */
  deliveredFps: number;
  /** ms since the compositor last composed a frame. */
  lastFrameAgeMs: number;
  skippedFrames: number;
  droppedOutputFrames: number;
  renderScale: number;
  contextLost: boolean;
  /** Frames per second that failed to compose (lost context, exception). */
  failedFps: number;
  /** Frames per second skipped because the main thread did not run in time. */
  starvedFps: number;
  /** State of the program video track. */
  trackState: MediaStreamTrackState | 'missing';
  trackMuted: boolean;
}

export interface AudioHealth {
  contextState: AudioContextState | 'interrupted' | 'missing';
  /** Audio-clock seconds advanced per wall-clock second over the last interval; ~1 when healthy. */
  clockRate: number;
  /** Render quanta processed per second as counted inside the audio thread; 0 means the graph is not being pulled. */
  quantaPerSecond: number;
  trackState: MediaStreamTrackState | 'missing';
  /** Master bus peak over the last interval, dBFS. */
  masterPeakDb: number;
}

export interface PeerHealth {
  peerId: string;
  connectionState: RTCPeerConnectionState;
  /** Video frames sent per second, from outbound-rtp deltas. */
  sentFps: number | null;
  /** Frames per second the receiver reports decoding; null when it does not report. */
  receivedFps: number | null;
  /** ms since the receiver decoded a new frame, extrapolated from its last report; null when unknown. */
  receiverFrameAgeMs: number | null;
  /** ms since the last receiver report arrived; null when it never reported. */
  reportAgeMs: number | null;
  freezeCount: number | null;
  audioFlowing: boolean | null;
  receiverAvSyncMs: number | null;
  rttMs: number | null;
  packetLossPct: number | null;
  outKbps: number;
  candidateType: string | null;
  qualityLimitationReason: string | null;
}

export interface VisibilityHealth {
  /** document.visibilityState */
  state: DocumentVisibilityState;
  /** ms the page has been continuously hidden; 0 when visible. */
  hiddenForMs: number;
  /** Whether the page reported being frozen/discarded-and-restored since monitoring began. */
  wasFrozen: boolean;
}

export interface HealthSample {
  /** Wall-clock ms (Date.now()). */
  at: number;
  /** ms since the previous sample as measured by the monitor's own clock; much more than the interval means the monitor itself was starved. */
  intervalMs: number;
  video: VideoHealth;
  audio: AudioHealth;
  /**
   * Capture-side A/V clock skew in ms: how far the audio clock moved relative to
   * the wall clock within the last ~30 s (positive = audio fell behind). Steady
   * device-clock drift stays small; a stall or skip of either clock shows up at
   * once and ages out with the window. The window restarts whenever the audio
   * context is not running, after a starved or frozen interval, after a stall
   * clears and when a live session starts; 0 while there is nothing to compare.
   */
  avSkewMs: number;
  peers: PeerHealth[];
  whip: PeerHealth | null;
  /**
   * The swarm segmenter's video encoder while swarm mode is live: frames waiting in it and frames
   * dropped since the previous sample. Absent or null when no segmenter runs.
   */
  encoder?: { queue: number; droppedDelta: number } | null;
  recording: { active: boolean; bytesPerSecond: number } | null;
  visibility: VisibilityHealth;
}

export type HealthIssueCode =
  | 'render-stall' // no frame composed and delivered for longer than the stall threshold
  | 'render-context-lost' // the GPU context is lost
  | 'main-thread-starved' // frames skipped because the page's main thread is not being run in time
  | 'low-fps' // sustained production well below target
  | 'output-dropping' // the output track is rejecting frames
  | 'encoder-overloaded' // the swarm segmenter's encoder is dropping frames or falling behind
  | 'video-track-ended'
  | 'audio-suspended' // context not running while something is live
  | 'audio-stall' // context "running" but its clock or graph is not advancing
  | 'audio-track-ended'
  | 'av-skew' // capture-side clocks have drifted apart beyond the threshold
  | 'no-outbound-video' // a connected peer is being sent no frames
  | 'receiver-frozen' // receiver reports no new frames while we believe we are sending
  | 'receiver-silent' // receiver reports no audio while the master bus is not silent
  | 'receiver-av-sync' // receiver-measured lip-sync error beyond the threshold
  | 'peer-disconnected'
  | 'peer-degraded' // high loss / RTT / cpu or bandwidth limited
  | 'recording-stalled' // recorder active but writing nothing, or recording frozen video
  | 'monitor-starved' // the watchdog's own clock was delayed (page throttled or frozen)
  | 'page-hidden'; // informational: live while the page is hidden

export interface HealthIssue {
  /** Stable while the condition persists: `${code}` or `${code}:${peerId}`. */
  id: string;
  code: HealthIssueCode;
  severity: 'info' | 'warning' | 'critical';
  /** Wall-clock ms when the condition was first seen. */
  since: number;
  message: string;
  peerId?: string;
  /** What the watchdog has done about it so far. */
  recovery: 'none' | 'attempting' | 'recovered' | 'failed';
}

export interface HealthEvent {
  at: number;
  type: 'issue-raised' | 'issue-cleared' | 'recovery-attempted' | 'recovery-succeeded' | 'recovery-failed';
  code: HealthIssueCode;
  peerId?: string;
  detail: string;
}

export interface HealthState {
  /** 'idle' when nothing is live; otherwise the worst active severity. */
  status: 'idle' | 'healthy' | 'warning' | 'critical';
  latest: HealthSample | null;
  issues: HealthIssue[];
  /** Most recent events, oldest first, bounded. */
  events: HealthEvent[];
}

export interface HealthThresholds {
  sampleIntervalMs: number;
  /** No composed frame for this long while live = render-stall. */
  renderStallMs: number;
  /** producedFps below targetFps * this ratio for lowFpsSeconds = low-fps. */
  lowFpsRatio: number;
  lowFpsSeconds: number;
  /** Receiver frame age beyond this = receiver-frozen. */
  receiverFrozenMs: number;
  avSkewMs: number;
  receiverAvSyncMs: number;
  /** A sample interval longer than this = monitor-starved. */
  starvedIntervalMs: number;
}

/** Everything the monitor reads or may act on. All getters must be cheap and must not throw. */
export interface HealthDeps {
  logger: Logger;
  getRenderStats(): RenderStats | null;
  /** performance.now()-based time of the last composed frame, or null before the first. */
  getLastFrameTime(): number | null;
  getProgramVideoTrack(): MediaStreamTrack | null;
  getProgramAudioTrack(): MediaStreamTrack | null;
  getAudioContext(): AudioContext | null;
  /** Cumulative render quanta counted in the audio thread (from the master meter worklet), or null if unavailable. */
  getAudioQuantaCount(): number | null;
  getMasterPeakDb(): number;
  /** Latest per-peer stats for P2P peers. */
  getPeerStats(): Promise<Record<string, PeerStats>>;
  getWhipStats(): Promise<PeerStats | null>;
  /** Swarm segmenter encoder counters (cumulative drops); null or absent when it is not running. */
  getEncoderStats?(): { queue: number; droppedFrames: number } | null;
  getRecorderBytes(): { active: boolean; bytesWritten: number } | null;
  /** True while a broadcast, WHIP session or recording is running. */
  isLive(): boolean;

  /** Recovery actions. Each resolves true if it believes it helped. */
  recover: {
    resumeAudio(): Promise<boolean>;
    /** Escalating compositor recovery (clock restart, context restore, output rebuild). */
    restartRenderLoop(): Promise<boolean>;
    /** Re-establish media for one peer (ICE restart or rebuild). */
    reconnectPeer(peerId: string): Promise<boolean>;
  };
}

export interface HealthMonitor {
  /** Begin sampling. Safe to call repeatedly. */
  start(): void;
  stop(): void;
  getState(): HealthState;
  subscribe(listener: (state: HealthState) => void): Unsubscribe;
  /** Fires for every raised/cleared issue and recovery attempt, for logs and notifications. */
  onEvent(listener: (event: HealthEvent) => void): Unsubscribe;
  /** Recent samples, oldest first (bounded ring, ~10 minutes), for diagnostics export. */
  getHistory(): readonly HealthSample[];
  destroy(): void;
}

export const DEFAULT_HEALTH_THRESHOLDS: HealthThresholds = {
  sampleIntervalMs: 1000,
  renderStallMs: 1500,
  lowFpsRatio: 0.8,
  lowFpsSeconds: 5,
  receiverFrozenMs: 3000,
  avSkewMs: 250,
  receiverAvSyncMs: 200,
  starvedIntervalMs: 3000,
};
